import { config } from '../config.ts';
import type { Mat4 } from '../util/mat.ts';
import { mat4LookAt, mat4Multiply, mat4Perspective } from '../util/mat.ts';
import type { Vec3 } from '../util/vec.ts';
import { v3add, v3cross, v3normalize, v3scale, v3sub } from '../util/vec.ts';
import { mulberry32 } from '../util/prng.ts';

export interface LookOffset {
  yaw: number;
  pitch: number;
  /** FOV multiplier: <1 zooms in (narrower FOV), >1 zooms out. Default 1. */
  fovScale?: number;
}

export interface CameraChoreographer {
  update(timeSec: number, aspect: number, lookOffset?: LookOffset): void;
  readonly viewProj: Mat4;
  readonly eye: Vec3;
  /** Final post-jitter, post-offset normalized look direction from the last update(). */
  readonly lookDir: Vec3;
}

export interface CreateOpts {
  aspect: number;
  seed?: number;
  heightSampler?: (worldX: number, worldZ: number) => number;
}

const FALLBACK_SEED = 0xa1b2c3d4;
const TWO_PI = Math.PI * 2;
const DEG2RAD = Math.PI / 180;
const FOV_Y_RAD = config.fovYDeg * DEG2RAD;
const UP_Y: Vec3 = [0, 1, 0];

const wrap = (i: number, n: number): number => ((i % n) + n) % n;

function catmullRom(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, t: number): Vec3 {
  const t2 = t * t;
  const t3 = t2 * t;
  const out: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < 3; i++) {
    const a = p0[i] as number;
    const b = p1[i] as number;
    const c = p2[i] as number;
    const d = p3[i] as number;
    out[i] = 0.5 * ((2 * b) + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
  }
  return out;
}

function sampleClosed(points: readonly Vec3[], u: number): Vec3 {
  const n = points.length;
  const seg = Math.floor(u);
  const t = u - seg;
  const i1 = wrap(seg, n);
  const i0 = wrap(seg - 1, n);
  const i2 = wrap(seg + 1, n);
  const i3 = wrap(seg + 2, n);
  return catmullRom(points[i0]!, points[i1]!, points[i2]!, points[i3]!, t);
}

function buildWaypoints(seed: number, count: number): Vec3[] {
  const rng = mulberry32(seed);
  const baseRadius = config.worldSize * 0.5;
  const seedOffset = rng() * TWO_PI;
  const points: Vec3[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * TWO_PI + seedOffset * 0.1;
    // Radial variety: some sweeps push deep inside, some pull far out.
    const radiusJitter = 0.55 + rng() * 0.6;
    const r = baseRadius * radiusJitter;
    // High-altitude cinematic flying: Y range ~[0.6, 1.4] * worldHeight.
    const y = config.worldHeight * (1.0 + 0.4 * Math.sin(angle * 2 + seedOffset));
    points.push([r * Math.cos(angle), y, r * Math.sin(angle)]);
  }
  return points;
}

function buildTargets(seed: number, count: number): Vec3[] {
  // Look targets ride a smaller-radius loop so the camera frames the city centre,
  // but the Y varies per waypoint to occasionally pick up peaks or look down streets.
  const rng = mulberry32((seed ^ 0xC0FFEE11) >>> 0);
  const r = config.worldSize * 0.12;
  const phaseOff = rng() * TWO_PI;
  const points: Vec3[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * TWO_PI + phaseOff;
    // Mix street-level (0.15) with snow-cap height (0.85) using a low-freq curve.
    const yBlend = 0.5 + 0.5 * Math.sin(angle * 1.5 + phaseOff * 0.7);
    const y = config.worldHeight * (0.15 + 0.7 * yBlend);
    points.push([r * Math.cos(angle), y, r * Math.sin(angle)]);
  }
  return points;
}

// Soft-max: smooth maximum of a and b with parameter k controlling the corner.
// Approaches max(a,b) as k→0; rounds the join when k is small-positive.
function softMax(a: number, b: number, k: number): number {
  const diff = a - b;
  return 0.5 * (a + b + Math.sqrt(diff * diff + k * k));
}

// Smooth-step ease applied to the loop phase to give variable speed: the camera
// lingers around each waypoint then accelerates between them. Built from a sum of
// sines whose period matches the waypoint count, so the eased phase still loops cleanly.
function easeLoopPhase(phase: number, waypointCount: number): number {
  // Ripple amplitude is small so the average speed is preserved; the camera spends
  // ~30% more time near waypoints than between them.
  const ripple = Math.sin(phase * TWO_PI * waypointCount) / (TWO_PI * waypointCount);
  return phase + ripple * 0.5;
}

export function createCameraChoreographer(opts: CreateOpts): CameraChoreographer {
  const seed = opts.seed ?? FALLBACK_SEED;
  const waypointCount = config.cameraWaypointCount;
  const targetCount = config.cameraTargetCount;
  const eyePoints = buildWaypoints(seed, waypointCount);
  const targetPoints = buildTargets(seed, targetCount);
  const heightSampler = opts.heightSampler;
  const loopSec = config.cameraLoopSeconds;

  let viewProj: Mat4 = new Float32Array(16);
  let eye: Vec3 = [0, 0, 0];
  let lookDir: Vec3 = [0, 0, -1];

  const update = (timeSec: number, aspect: number, lookOffset?: LookOffset): void => {
    const rawPhase = (((timeSec % loopSec) + loopSec) % loopSec) / loopSec;
    const phase = easeLoopPhase(rawPhase, waypointCount);

    const eyeU = phase * eyePoints.length;
    const targetU = phase * targetPoints.length;

    eye = sampleClosed(eyePoints, eyeU);
    let target = sampleClosed(targetPoints, targetU);

    if (heightSampler) {
      // Soft-max against terrain + clearance — smooths the kink where the spline
      // would otherwise dip through the ground.
      const groundEye = heightSampler(eye[0], eye[2]);
      const minEyeY = groundEye + config.cameraEyeClearance;
      const eyeY = softMax(eye[1], minEyeY, 8);
      eye = [eye[0], eyeY, eye[2]];

      const groundTarget = heightSampler(target[0], target[2]);
      const minTargetY = groundTarget + config.cameraTargetClearance;
      const targetY = softMax(target[1], minTargetY, 6);
      target = [target[0], targetY, target[2]];
    }

    // Effective look offset = mouse offset + a constant downward tilt, so the
    // camera always frames a bit more top-down even with no user input.
    const effYaw = lookOffset?.yaw ?? 0;
    const effPitch = (lookOffset?.pitch ?? 0) - config.cameraDownwardPitch;
    if (effYaw !== 0 || effPitch !== 0) {
      const dir = v3sub(target, eye);
      const dist = Math.hypot(dir[0], dir[1], dir[2]);
      if (dist > 1e-4) {
        let nx = dir[0] / dist;
        let ny = dir[1] / dist;
        let nz = dir[2] / dist;
        // Yaw around world Y.
        const cy = Math.cos(effYaw);
        const sy = Math.sin(effYaw);
        const yx = nx * cy + nz * sy;
        const yz = -nx * sy + nz * cy;
        nx = yx; nz = yz;
        // Pitch around camera right (post-yaw). Right ⟂ dir, so Rodrigues simplifies.
        const n: Vec3 = [nx, ny, nz];
        const right = v3normalize(v3cross(n, UP_Y));
        const cross = v3cross(right, n);
        const cp = Math.cos(effPitch);
        const sp = Math.sin(effPitch);
        nx = nx * cp + cross[0] * sp;
        ny = ny * cp + cross[1] * sp;
        nz = nz * cp + cross[2] * sp;
        target = [eye[0] + nx * dist, eye[1] + ny * dist, eye[2] + nz * dist];
      }
    }

    // Drive target jitter from loop phase, not absolute time, so the same point in
    // the loop replays identically across reloads and the wobble doesn't grow with
    // page lifetime. Small amplitude — sells "handheld" without reading as look-target jitter.
    const wPhase = rawPhase * TWO_PI;
    const wx = Math.sin(wPhase * 5.0) * Math.sin(wPhase * 2.2) * 0.0015;
    const wy = Math.sin(wPhase * 3.7 + 1.7) * Math.cos(wPhase * 3.1) * 0.0012;

    const preJitterDir = v3normalize(v3sub(target, eye));
    const right = v3normalize(v3cross(preJitterDir, UP_Y));
    const up = v3cross(right, preJitterDir);

    target = v3add(target, v3add(v3scale(right, wx * config.worldSize), v3scale(up, wy * config.worldSize)));

    const fovScale = lookOffset?.fovScale ?? 1;
    const view = mat4LookAt(eye, target, UP_Y);
    const proj = mat4Perspective(FOV_Y_RAD * fovScale, aspect, config.near, config.far);
    viewProj = mat4Multiply(proj, view);
    lookDir = v3normalize(v3sub(target, eye));
  };

  update(0, opts.aspect);

  return {
    update,
    get viewProj(): Mat4 {
      return viewProj;
    },
    get eye(): Vec3 {
      return eye;
    },
    get lookDir(): Vec3 {
      return lookDir;
    },
  };
}
